import moment from 'moment';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useRowSelect, useTable } from 'react-table';
import { toast } from 'react-toastify';
import { useAppContext } from '../../appProvider/contextProvider';
import DeletePrompt from '../../components/Propmts/deletePrompt';
import { getSubscribers, updateSubscribers, deleteSubscribers } from '../../services/subscribersService';
import NoDataImage from '../../assets/images/illustration.svg';
import HelperComponent from '../../components/HelperComponent/HelperComponent';


const columns =  [
    {
        Header: 'ID',
        accessor: 'id', // accessor is the "key" in the data
    },
    {
        Header: 'Platform',
        accessor: 'platform', // accessor is the "key" in the data
    },
    {
        Header: 'Country',
        accessor: 'country_name', // accessor is the "key" in the data
    },
    {
        Header: 'Browser',
        accessor: 'browser', // accessor is the "key" in the data
    },
    {
        Header: 'Active',
        accessor: 'is_active', // accessor is the "key" in the data
        Cell: (props) => (
            props.value ?  <i className="icon icon-check"></i> : <i className="icon icon-close"></i> 
        )
    },
    {
        Header: 'Test Device',
        accessor: 'is_test_device',
        Cell: (props) => (
            props.value ?  <i className="icon icon-check"></i> : <i className="icon icon-close"></i> 
        )
    },
    {
        Header: 'Created',
        accessor: 'created_at', // accessor is the "key" in the data
        Cell: (props) => moment(props.value).format('MMM DD, YYYY'),
    },
]

const IndeterminateCheckbox = forwardRef(
    ({ indeterminate, id, ...rest }, ref) => {
      const defaultRef = useRef()
      const resolvedRef = ref || defaultRef
  
      useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate
      }, [resolvedRef, indeterminate])
  
      return (
        <div className="custom-control custom-checkbox">
            <input type="checkbox" className="custom-control-input" id={id} ref={resolvedRef} {...rest} />
            <label className="custom-control-label" htmlFor={id}></label>
        </div>
      )
    }
  )

const Subscribers = () => {

    const [isPreviousEnable, setIsPreviousEnable] = useState(true);
    const [isNextEnable, setIsNextEnable] = useState(true);
    const [count, setCount] = useState(0);
    const [subscribersData, setSubscribersData] = useState([]);
    const { selectedWebsite, 
        isFetching, 
        isMutating, 
        setIsBgDisabled
    } = useAppContext();

    const [testDeviceFilter, setTestDeviceFilter] = useState(false);
    const [safariFilter, setSafariFilter] = useState(false);

    const [page, setPage] = useState(1);

    const [dropDownFilter, setDropDownFilter] = useState('all');

    const [deletePrompt, setDeletePrompt] = useState(false);
    
    useQuery(['subscribers', {
        webId: selectedWebsite?.id,
        page: page,
        page_size: 20,
        broswer: safariFilter ? 'Safari' : null,
        is_test_device: testDeviceFilter,
        is_active: dropDownFilter,
    }], () => {
        const queryParams = {
            webId: selectedWebsite?.id,
            page: page,
            page_size: 20
        };
        if(safariFilter) {
            queryParams.browser = 'Safari'
        }
        if(testDeviceFilter) {
            queryParams.is_test_device = true;
        }
        if(dropDownFilter !== 'all') {
            queryParams.is_active = dropDownFilter === 'active';
        }
        return  getSubscribers(queryParams);
    }, {
        enabled: !!selectedWebsite,
        onSuccess: (subscribers) => {
            setCount(subscribers.count);
            setIsPreviousEnable(!!subscribers.previous);
            setIsNextEnable(!!subscribers.next);
            setSubscribersData(subscribers.results);
        }
    });
    
    const subscribersInstance = useTable({
            columns,
            data: subscribersData,
        }, 
        useRowSelect,
        hooks => {
            hooks.visibleColumns.push(columns => [
              // Let's make a column for selection
              {
                id: 'selection',
                // The header can use the table's getToggleAllRowsSelectedProps method
                // to render a checkbox
                Header: ({ getToggleAllRowsSelectedProps }) => (
                  <div>
                    <IndeterminateCheckbox id={'header'} {...getToggleAllRowsSelectedProps()} />
                  </div>
                ),
                // The cell can use the individual row's getToggleRowSelectedProps method
                // to the render a checkbox
                Cell: ({ row }) =>(
                  <div>
                    <IndeterminateCheckbox id={row?.values?.id} {...row.getToggleRowSelectedProps()} />
                  </div>
                ),
              },
              ...columns,
            ])
        });

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        selectedFlatRows,
      } = subscribersInstance;
      

      const addToTestDevices = useMutation((event) => {
          event.preventDefault();
          const subscription_ids = selectedFlatRows.map(({values}) => values.id);
          return updateSubscribers(selectedWebsite?.id, {
              subscription_ids,
              is_test_device : true,
          })
      }, {
          onSuccess: ((updateResponse) => {
            if(updateResponse.errors) {
                toast.error(updateResponse.errors);
            } else {
                const subscription_ids = selectedFlatRows.map(({values}) => values.id);
                const tempSubscriptions = subscribersData.map((subscriberData) => {
                    if(subscription_ids.includes(subscriberData.id)) {
                        subscriberData.is_test_device = true;
                    }
                    return subscriberData;
                })
                setSubscribersData(tempSubscriptions);
                toast.success('Subscribers Added to test device');
            }
          })
      });

      const removeFromTestDevices = useMutation((event) => {
        event.preventDefault();
        const subscription_ids = selectedFlatRows.map(({values}) => values.id);
        return updateSubscribers(selectedWebsite?.id, {
            subscription_ids,
            is_test_device : false,
        })
    }, {
        onSuccess: ((updateResponse) => {
          if(updateResponse.errors) {
              toast.error(updateResponse.errors);
          } else {
              const subscription_ids = selectedFlatRows.map(({values}) => values.id);
              const tempSubscriptions = subscribersData.map((subscriberData) => {
                  if(subscription_ids.includes(subscriberData.id)) {
                      subscriberData.is_test_device = false;
                  }
                  return subscriberData;
              })
              setSubscribersData(tempSubscriptions);
              toast.success('Subscribers Removed from test device');
          }
        })
    });


      const deleteSubscriber = useMutation(() => {
        return deleteSubscribers(selectedWebsite?.id)
    }, {
        onSuccess: ((updateResponse) => {
          if(updateResponse.errors) {
              toast.error(updateResponse.errors);
          } else {
              const tempSubscriptions = subscribersData.filter(({is_active}) => is_active);
              setSubscribersData(tempSubscriptions);
              toast.success('Subscribers deletion is initiated it may take a while!');
          }
        })
    });


    const deleteInactiveSubscribers = () => {
        setDeletePrompt(true);
        setIsBgDisabled(true)
    }
    
    const onDelete = (event) => {
        event.preventDefault();
        setDeletePrompt(false);
        setIsBgDisabled(false)
        deleteSubscriber.mutate();
    }

    const onCancel = () =>{
        setDeletePrompt(false); 
        setIsBgDisabled(false);
    }

    return <>
        { deletePrompt && <DeletePrompt onDelete={onDelete} onCancel={onCancel} />}
        <div className="panel full-height scrollabel_y">
            <div className="panel-header">
                <div className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">
                        Subscribers
                        <HelperComponent
                            component={'Subscribers'}  
                            href="https://chirpyweb.com/subscribers/" />
                    </h3>
                </div>                                
            </div>
            <div className="panel-body d-flex flex-column full-height scrollable_y">
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <div className="d-flex flex-wrap">
                        <div className="form-group">
                            <div className="custom-select">
                                <select 
                                    value={dropDownFilter} 
                                    className="form-control"
                                    onChange={({target}) => {setPage(1); setDropDownFilter(target.value)}}
                                >
                                    <option value="all" >All Subscribers</option>
                                    <option value="active">Active Subscribers</option>
                                    <option value="inactive">Inactive Subscribers</option>
                                </select>
                            </div>
                        </div>
                        <label className="toggle-control mx-4 mt-2"> Safari
                            <input type="checkbox" checked={safariFilter} onChange={() => { setPage(1);setSafariFilter(!safariFilter)}} />
                            <span className="control"></span>
                        </label>
                        <label className="toggle-control toggle-control-md mt-2"> Only Test Devices
                            <input type="checkbox" checked={testDeviceFilter} onChange={() => {setPage(1);setTestDeviceFilter(!testDeviceFilter)}} />
                            <span className="control"></span>
                        </label>
                    </div>                                
                    <div className="my-2">
                        {
                            !testDeviceFilter ?
                                <button className="btn btn-primary mr-2" disabled={selectedFlatRows.length <= 0} onClick={addToTestDevices.mutate}>Add To Test Devices</button>
                            :
                                <button className="btn btn-primary mr-2" disabled={selectedFlatRows.length <= 0} onClick={removeFromTestDevices.mutate}>Remove From Test Devices</button>
                        }
                        {(selectedWebsite?.role !== 'view' &&  dropDownFilter === 'inactive') && <button className="btn btn-danger" onClick={deleteInactiveSubscribers}><i className="icon icon-delete mr-2"></i>Delete All Inactive Subscribers</button>}
                    </div>
                </div>
                <div className="table-responsive full-height">
                    <table className="table table-hover subscribers-table" {...getTableProps()} >
                        <thead>
                            {
                                headerGroups.map(headerGroup => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {
                                            headerGroup.headers.map(column => (
                                                <th {...column.getHeaderProps()}>
                                                    {column.render('Header')}
                                                </th>
                                            ))
                                        }
                                    </tr>
                                ))
                            }
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {   // Loop over the table rows
                                rows.map(row => {
                                    // Prepare the row for display
                                    prepareRow(row)
                                    return (
                                    // Apply the row props
                                    <tr {...row.getRowProps()}>
                                        {// Loop over the rows cells
                                        row.cells.map(cell =>  <td {...cell.getCellProps()}>
                                        {// Render the cell contents
                                        cell.render('Cell')}
                                        </td>)}
                                    </tr>
                                    )
                                })
                            }
                            {
                                        (!isFetching && !isMutating && subscribersData.length <= 0) &&  <tr>
                                            <td colSpan="7" align="center">
                                                <div className="d-flex flex-column align-items-center justify-content-center mt-5">
                                                    <img src={NoDataImage} className="illustration-image" />
                                                    <h5 className="medium text-dark mt-2">No subscribers found yet</h5>
                                                </div>                                                    
                                            </td>
                                        </tr>
                                    }
                        </tbody>
                    </table>                                           
                </div> 
                <div className="d-flex justify-content-between align-items-center">
                    <p>Total {  (((page - 1) * 20) + rows.length)} / {count} entries</p>
                    <div>
                        <button className="btn mr-2" disabled={!isPreviousEnable} onClick={() => setPage(page - 1)}><i className="icon icon-chevron-left mr-2"></i>Prev</button>
                        <button className="btn " disabled={!isNextEnable} onClick={() => setPage(page + 1)} >Next <i className="icon icon-chevron-right ml-2"></i></button>
                    </div>
                </div>                
            </div>
        </div>

    </>
};


export default Subscribers;